import React, { createContext, useContext } from 'react';
import { View, Text, TouchableOpacity, Pressable } from 'react-native';
import FullscreenSafeModal from '../FullscreenSafeModal';
import { CANVA_NAV_BLUE, CANVA_TITLE_CREAM } from '../../utils/hamburgerMenu';
import { getMapPromptCopy } from '../../utils/mapAccess';

/**
 * Side-rail forms ask this before opening. Returns true when the viewer may contribute;
 * otherwise TourPlanner shows the sign-in prompt and it returns false. Defaults to allow
 * so the rail works unchanged outside TourPlanner.
 */
export const MapContributorGateContext = createContext(() => true);

export function useMapContributorGate() {
  return useContext(MapContributorGateContext);
}

/**
 * Sign-in / membership prompt for map actions guests or free members can't take.
 * `prompt` is { gate: 'signIn' | 'subscribe', kind } or null.
 */
export default function MapAccessPrompt({ prompt, onClose, navigation, isDarkMode, onBeforeNavigate }) {
  const visible = !!prompt;
  const copy = prompt ? getMapPromptCopy(prompt.gate, prompt.kind) : null;
  const accent = isDarkMode ? CANVA_TITLE_CREAM : CANVA_NAV_BLUE;

  // Leave fullscreen first, or the next screen opens behind the map.
  const go = async (screen, params) => {
    onClose();
    try { await onBeforeNavigate?.(); } catch (_) {}
    navigation.navigate(screen, params);
  };

  return (
    <FullscreenSafeModal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        accessibilityLabel="Close"
        style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        onPress={onClose}
      >
        {copy && (
          <Pressable
            accessibilityRole="dialog"
            style={{ backgroundColor: isDarkMode ? '#1a1a2e' : '#ffffff', borderRadius: 20, width: '100%', maxWidth: 400, overflow: 'hidden' }}
            onPress={() => {}}
          >
            <View style={{ backgroundColor: CANVA_NAV_BLUE, padding: 24, alignItems: 'center' }}>
              <Text accessibilityRole="header" style={{ color: '#fff', fontSize: 22, fontWeight: '800', textAlign: 'center', fontFamily: 'VastagoGrotesk' }}>
                {copy.title}
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, marginTop: 6, textAlign: 'center', lineHeight: 20 }}>
                {copy.body}
              </Text>
            </View>
            <View style={{ padding: 24, gap: 12 }}>
              {prompt.gate === 'subscribe' ? (
                <TouchableOpacity
                  style={{ backgroundColor: CANVA_NAV_BLUE, borderRadius: 12, paddingVertical: 14, alignItems: 'center' }}
                  onPress={() => go('Plans')}
                >
                  <Text style={{ color: '#fff', fontSize: 16, fontWeight: '700' }}>{copy.primary}</Text>
                </TouchableOpacity>
              ) : (
                <>
                  <TouchableOpacity
                    style={{ backgroundColor: CANVA_NAV_BLUE, borderRadius: 12, paddingVertical: 14, alignItems: 'center' }}
                    onPress={() => go('Login', { isSignUp: true, returnTo: 'TourPlanner', returnBack: true })}
                  >
                    <Text style={{ color: '#fff', fontSize: 16, fontWeight: '700' }}>{copy.primary}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={{ borderWidth: 1.5, borderColor: accent, borderRadius: 12, paddingVertical: 14, alignItems: 'center' }}
                    onPress={() => go('Login', { returnTo: 'TourPlanner', returnBack: true })}
                  >
                    <Text style={{ color: accent, fontSize: 16, fontWeight: '700' }}>Sign In</Text>
                  </TouchableOpacity>
                </>
              )}
              <TouchableOpacity style={{ paddingVertical: 10, alignItems: 'center' }} onPress={onClose}>
                <Text style={{ color: isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)', fontSize: 14 }}>Keep exploring</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        )}
      </Pressable>
    </FullscreenSafeModal>
  );
}
